/**
 * i18n layer.
 *
 * Every string in the app flows through `useI18n().t` (a strongly-typed
 * `Dictionary`), so adding a locale is purely a matter of dropping a
 * `<locale>.ts` file and registering it below — no component changes.
 *
 * - Slovak (`sk`) is the SOURCE dictionary; `Dictionary = typeof sk`.
 * - The other locales currently re-export `sk` as a stub (they render Slovak)
 *   until translated. Replacing the stub body translates the app for free.
 * - Active locale resolution, in priority order: URL `?lang=<code>` →
 *   shared `solandio_lang` cookie → `localStorage` → browser language → `sk`.
 *   It's changeable at runtime via `setLocale`, which re-renders the whole tree
 *   and persists to BOTH the cross-subdomain cookie AND `localStorage`, and
 *   updates `<html lang>`.
 *
 * Cross-subdomain carry-over: the landing (solandio.com) and the app
 * (app.solandio.com) are separate apps on one parent domain. `localStorage` is
 * NOT shared across subdomains, so the carry mechanism is a cookie scoped to
 * `Domain=.solandio.com` (leading dot → shared by every *.solandio.com host).
 * Both apps read/write the SAME cookie name + value contract.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { sk, type Dictionary } from './sk';
import { en } from './en';
import { de } from './de';
import { cs } from './cs';
import { pl } from './pl';
import { hu } from './hu';
import { fr } from './fr';

export type Locale = 'sk' | 'en' | 'de' | 'cs' | 'pl' | 'hu' | 'fr';

export const DEFAULT_LOCALE: Locale = 'sk';

/** Registry of every shipped dictionary, keyed by locale code. */
const dictionaries: Record<Locale, Dictionary> = {
  sk,
  en,
  de,
  cs,
  pl,
  hu,
  fr,
};

/**
 * Switcher options, in display order. `label` is the endonym (the accessible
 * name — flags alone are NOT accessible); `flag` is a decorative emoji rendered
 * aria-hidden alongside it. `short` is the upper-cased code shown on the compact
 * trigger.
 */
export const LOCALES: readonly {
  readonly code: Locale;
  readonly label: string;
  readonly flag: string;
  readonly short: string;
}[] = [
  { code: 'sk', label: 'Slovenčina', flag: '🇸🇰', short: 'SK' },
  { code: 'en', label: 'English', flag: '🇬🇧', short: 'EN' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪', short: 'DE' },
  { code: 'cs', label: 'Čeština', flag: '🇨🇿', short: 'CS' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱', short: 'PL' },
  { code: 'hu', label: 'Magyar', flag: '🇭🇺', short: 'HU' },
  { code: 'fr', label: 'Français', flag: '🇫🇷', short: 'FR' },
];

/** Returns the dictionary for a locale, falling back to the source (`sk`). */
export function getDictionary(locale: Locale = DEFAULT_LOCALE): Dictionary {
  return dictionaries[locale] ?? sk;
}

const STORAGE_KEY = 'solandio:locale';

/**
 * Shared, cross-subdomain language cookie. Scoped to the parent domain (leading
 * dot) so the choice carries between solandio.com / www / app.solandio.com.
 * Both Solandio apps MUST use this exact name + attribute contract.
 */
const COOKIE_NAME = 'solandio_lang';
const COOKIE_DOMAIN = '.solandio.com';
const COOKIE_MAX_AGE = 31536000; // 1 year, in seconds.

/** The `?lang=` query param honoured on init for landing → app carry-over. */
const URL_PARAM = 'lang';

function isLocale(value: string | null | undefined): value is Locale {
  return value != null && value in dictionaries;
}

/** Reads the shared `solandio_lang` cookie, or null when absent/unreadable. */
function readLocaleCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${COOKIE_NAME}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

/**
 * Writes the shared `solandio_lang` cookie on the parent domain so the choice
 * is visible to every *.solandio.com host. `Secure` is omitted on localhost /
 * plain-HTTP dev (a Secure cookie would be silently dropped there).
 */
function writeLocaleCookie(locale: Locale): void {
  if (typeof document === 'undefined') return;
  const secure =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? '; Secure'
      : '';
  // The Domain attribute only applies on the real parent domain; on localhost
  // it's ignored by the browser, so the same code path is safe in dev.
  document.cookie =
    `${COOKIE_NAME}=${encodeURIComponent(locale)}` +
    `; Domain=${COOKIE_DOMAIN}; Path=/; Max-Age=${COOKIE_MAX_AGE}` +
    `; SameSite=Lax${secure}`;
}

/** Reads a valid `?lang=<code>` from the current URL, or null. */
function readLocaleFromUrl(): Locale | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = new URLSearchParams(window.location.search).get(URL_PARAM);
    return isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Resolves the initial locale per the carry-over contract, in priority order:
 *   1. URL `?lang=<code>` (and persist it, so the carry survives navigation),
 *   2. shared `solandio_lang` cookie,
 *   3. existing `localStorage` value,
 *   4. browser language (2-letter prefix) mapped to a supported locale,
 *   5. default (`sk`).
 */
function resolveInitialLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  // (1) URL param wins and is adopted as the persisted choice.
  const fromUrl = readLocaleFromUrl();
  if (fromUrl) {
    persistLocale(fromUrl);
    return fromUrl;
  }

  // (2) Cross-subdomain cookie (the landing → app carry mechanism).
  const fromCookie = readLocaleCookie();
  if (isLocale(fromCookie)) return fromCookie;

  // (3) Existing per-origin localStorage preference.
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // localStorage can throw (private mode / disabled); fall through.
  }

  // (4) Browser language.
  const browser = window.navigator.language?.slice(0, 2).toLowerCase();
  if (isLocale(browser)) return browser;

  // (5) Default.
  return DEFAULT_LOCALE;
}

/** Persists the locale to BOTH the shared cookie and per-origin localStorage. */
function persistLocale(locale: Locale): void {
  writeLocaleCookie(locale);
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Persistence is best-effort; ignore storage failures.
  }
}

interface I18nValue {
  locale: Locale;
  t: Dictionary;
  setLocale: (locale: Locale) => void;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({
  children,
  locale: forcedLocale,
}: {
  children: ReactNode;
  /** Optional fixed locale (e.g. tests). Omit to use browser/localStorage. */
  locale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(
    () => forcedLocale ?? resolveInitialLocale(),
  );

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    // Persist to BOTH the shared cookie (cross-subdomain carry) and
    // localStorage (per-origin), then reflect in <html lang>.
    persistLocale(next);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = next;
    }
  }, []);

  const value = useMemo<I18nValue>(
    () => ({ locale, t: getDictionary(locale), setLocale }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within <I18nProvider>.');
  return ctx;
}

export type { Dictionary } from './sk';
